import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Icono, IconBubble, Avatar } from "./iconos";

const VERDE = "#7BC74D";

const fmt = (n, dec = 0) =>
    n == null ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

function PctBadge({ pct, ppMode }) {
    if (pct == null) return <span style={{ fontSize: "0.72rem", color: "#bbb" }}>—</span>;
    const up = pct >= 0;
    const suffix = ppMode ? "p.p." : "%";
    return (
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: up ? "#3a7d1e" : "#c0392b" }}>
            {up ? "+" : ""}{pct} {suffix}
            <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 4 }}>vs período anterior</span>
        </span>
    );
}

function KpiCard({ icono, label, value, pct, color, sub, ppMode }) {
    const c = color || VERDE;
    return (
        <div className="col-6 col-md-4 col-lg mb-3">
            <div className="card h-100 border-0 shadow-sm">
                <div className="card-body py-3 px-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <IconBubble color={c} size={32}>{icono}</IconBubble>
                        <span className="text-muted" style={{ fontSize: "0.74rem", fontWeight: 500 }}>{label}</span>
                    </div>
                    <div style={{ fontSize: "1.55rem", fontWeight: 700, color: "#222", lineHeight: 1.1, marginBottom: 4 }}>
                        {value}
                    </div>
                    {sub
                        ? <div className="text-muted" style={{ fontSize: "0.7rem" }}>{sub}</div>
                        : <PctBadge pct={pct} ppMode={ppMode} />}
                </div>
            </div>
        </div>
    );
}

export default function ZTClosers() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [datos, setDatos] = useState(null);

    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) { navegacion("/"); return; }
            try {
                const r = await fetch(process.env.REACT_APP_API_ZT_CLOSERS, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) { navegacion("/"); return; }
                if (r.ok) { setDatos(await r.json()); setAutorizado(true); }
            } catch { navegacion("/"); }
            finally { setCargando(false); }
        };
        init();
    }, [navegacion]);

    if (cargando) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="spinner-border" style={{ color: VERDE }} />
        </div>
    );
    if (!autorizado || !datos) return null;

    const resumen = datos.resumen || [];
    const asignados = resumen.filter(r => r.idCloser !== "(sin asignar)");

    // KPIs globales
    const closersActivos = asignados.length;
    const totalLeads     = asignados.reduce((s, r) => s + r.totalLeads, 0);
    const totalCont      = asignados.reduce((s, r) => s + r.contactados + r.enSeguimiento + r.cerrados, 0);
    const totalCalif     = asignados.reduce((s, r) => s + r.enSeguimiento + r.cerrados, 0);
    const totalCerr      = asignados.reduce((s, r) => s + r.cerrados, 0);
    const contactabilidad = totalLeads > 0 ? Math.round(totalCont / totalLeads * 1000) / 10 : 0;
    const cierreDemo      = totalCont > 0 ? Math.round(totalCalif / totalCont * 1000) / 10 : 0;
    const cierreFinal     = totalLeads > 0 ? Math.round(totalCerr / totalLeads * 1000) / 10 : 0;

    // Oportunidades de mejora dinámicas
    const oportunidades = [];
    if (cierreFinal < 10)
        oportunidades.push({ titulo: "Mejorar conversión final", detalle: "La tasa de cierre está por debajo del objetivo del 15%.", prioridad: "Alta", icono: <Icono.Target /> });
    if (contactabilidad < 60)
        oportunidades.push({ titulo: "Aumentar contactabilidad", detalle: "Más del 40% de los leads no fueron contactados aún.", prioridad: "Alta", icono: <Icono.Phone /> });
    oportunidades.push({ titulo: "Responder más rápido", detalle: "Reducir el tiempo de respuesta por debajo de 15 min.", prioridad: "Alta", icono: <Icono.Clock /> });
    oportunidades.push({ titulo: "Pausar ubicaciones débiles", detalle: "Excluir ubicaciones con bajo rendimiento y alto CPL.", prioridad: "Alta", icono: <Icono.MapPin /> });
    oportunidades.push({ titulo: "Duplicar creativos ganadores", detalle: "Escalar los anuncios con mejor CTR y menor CPL.", prioridad: "Media", icono: <Icono.Trophy /> });
    oportunidades.push({ titulo: "Segmentar 25–54", detalle: "Enfocar presupuesto en el segmento con mejor conversión.", prioridad: "Media", icono: <Icono.Users /> });
    oportunidades.push({ titulo: "Mejorar seguimiento de leads", detalle: "Más toques y mensajes personalizados para cerrar más.", prioridad: "Baja", icono: <Icono.CheckCircle /> });

    const PRIORIDAD_BADGE = {
        Alta:  { bg: "#fdecea", color: "#c0392b" },
        Media: { bg: "#fff3cd", color: "#856404" },
        Baja:  { bg: "#e9ecef", color: "#6c757d" },
    };

    // Checklist de calidad: por ahora, todos completos (campos disponibles en el modelo)
    const checklist = [
        { label: "Origen", icono: <Icono.MapPin /> },
        { label: "Campaña", icono: <Icono.Filter /> },
        { label: "Anuncio", icono: <Icono.ImageIcon /> },
        { label: "Fecha de lead", icono: <Icono.Calendar /> },
        { label: "Primer contacto", icono: <Icono.Phone /> },
        { label: "Estado", icono: <Icono.CheckCircle /> },
        { label: "Motivo de pérdida", icono: <Icono.XCircle /> },
        { label: "Presupuesto estimado", icono: <Icono.Dollar /> },
        { label: "Tipo de evento", icono: <Icono.Star /> },
        { label: "Fecha del evento", icono: <Icono.Calendar /> },
    ];

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#222" }}>Closers y oportunidades de mejora</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Rendimiento del equipo de closers y acciones para seguir mejorando.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.78rem" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: VERDE }} />
                    Actualizado hace 5 min
                </div>
            </div>

            {/* KPIs */}
            <div className="row g-2 mb-3">
                <KpiCard icono={<Icono.Users />}        label="Closers activos"          value={closersActivos}          sub="del equipo de ventas" color={VERDE} />
                <KpiCard icono={<Icono.Clock />}        label="Tiempo medio de respuesta" value="—"                       pct={null}                 color={VERDE} />
                <KpiCard icono={<Icono.Phone />}        label="Contactabilidad"          value={`${fmt(contactabilidad, 1)}%`} pct={null}             color={VERDE} ppMode />
                <KpiCard icono={<Icono.Target />}       label="Cierre a demo"            value={`${fmt(cierreDemo, 1)}%`}      pct={null}             color={VERDE} ppMode />
                <KpiCard icono={<Icono.CheckCircle />}  label="Cierre final"             value={`${fmt(cierreFinal, 1)}%`}     pct={null}             color={VERDE} ppMode />
            </div>

            {/* Tabla rendimiento + Checklist */}
            <div className="row g-3 mb-3">
                <div className="col-12 col-lg-8">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">Rendimiento por closer</h6>
                                <button className="btn btn-sm" style={{ fontSize: "0.75rem", color: VERDE, fontWeight: 600 }}>Ver detalle por closer →</button>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid #eee" }}>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999", paddingBottom: 10 }}>Closer</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Leads asignados</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Contactados</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Calificados</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Cerrados</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Tiempo respuesta</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resumen.length === 0 && (
                                            <tr><td colSpan={6} className="text-center text-muted py-4">Sin datos</td></tr>
                                        )}
                                        {resumen.map((r, i) => {
                                            const esSinAsignar = r.idCloser === "(sin asignar)";
                                            const nombre = esSinAsignar ? "Sin asignar" : `Closer ${r.idCloser}`;
                                            const contPct  = r.totalLeads > 0 ? (r.contactados + r.enSeguimiento + r.cerrados) / r.totalLeads * 100 : 0;
                                            const califPct = r.totalLeads > 0 ? (r.enSeguimiento + r.cerrados) / r.totalLeads * 100 : 0;
                                            const cerrPct  = r.totalLeads > 0 ? r.cerrados / r.totalLeads * 100 : 0;
                                            return (
                                                <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                    <td style={{ padding: "10px 0" }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {!esSinAsignar && <Avatar nombre={nombre} size={28} />}
                                                            <span style={{ fontSize: "0.82rem", fontWeight: 600, color: esSinAsignar ? "#888" : "#222" }}>
                                                                {nombre}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="text-end" style={{ fontSize: "0.82rem", fontWeight: 600 }}>{r.totalLeads}</td>
                                                    <td className="text-end" style={{ fontSize: "0.78rem", color: "#666" }}>
                                                        {r.contactados + r.enSeguimiento + r.cerrados} ({fmt(contPct, 1)}%)
                                                    </td>
                                                    <td className="text-end" style={{ fontSize: "0.78rem", color: "#666" }}>
                                                        {r.enSeguimiento + r.cerrados} ({fmt(califPct, 1)}%)
                                                    </td>
                                                    <td className="text-end" style={{ fontSize: "0.78rem", color: "#666" }}>
                                                        {r.cerrados} ({fmt(cerrPct, 1)}%)
                                                    </td>
                                                    <td className="text-end" style={{ fontSize: "0.78rem", color: "#3a7d1e", fontWeight: 600 }}>—</td>
                                                </tr>
                                            );
                                        })}
                                        {asignados.length > 1 && (
                                            <tr style={{ borderTop: "2px solid #eee" }}>
                                                <td style={{ padding: "10px 0", fontWeight: 700, fontSize: "0.85rem" }}>Total</td>
                                                <td className="text-end" style={{ fontSize: "0.85rem", fontWeight: 700 }}>{totalLeads}</td>
                                                <td className="text-end" style={{ fontSize: "0.78rem", fontWeight: 700 }}>{totalCont} ({fmt(contactabilidad, 1)}%)</td>
                                                <td className="text-end" style={{ fontSize: "0.78rem", fontWeight: 700 }}>{totalCalif}</td>
                                                <td className="text-end" style={{ fontSize: "0.78rem", fontWeight: 700 }}>{totalCerr} ({fmt(cierreFinal, 1)}%)</td>
                                                <td className="text-end" style={{ fontSize: "0.78rem", fontWeight: 700 }}>—</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-1">Checklist de calidad de datos</h6>
                            <p className="text-muted mb-3" style={{ fontSize: "0.74rem" }}>
                                Campos que rastreamos en el CRM para asegurar datos completos y confiables.
                            </p>
                            {checklist.map((c, i) => (
                                <div key={i} className="d-flex justify-content-between align-items-center py-1" style={{ borderBottom: i < checklist.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ color: "#666", display: "inline-flex" }}>{c.icono}</span>
                                        <span style={{ fontSize: "0.78rem" }}>{c.label}</span>
                                    </div>
                                    <span style={{
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                        padding: "2px 8px",
                                        borderRadius: 12,
                                        backgroundColor: "#e8f5e0",
                                        color: "#3a7d1e",
                                    }}>Completo</span>
                                </div>
                            ))}
                            <button className="btn btn-sm w-100 mt-3" style={{
                                fontSize: "0.78rem",
                                border: "1px solid #ddd",
                                color: "#555",
                                fontWeight: 500,
                            }}>
                                Ver auditoría de datos →
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Oportunidades de mejora */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="mb-3">
                        <h6 className="fw-bold mb-0">Oportunidades de mejora</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "0.78rem" }}>
                            Acciones priorizadas para aumentar cierres y eficiencia.
                        </p>
                    </div>
                    {oportunidades.map((o, i) => {
                        const p = PRIORIDAD_BADGE[o.prioridad];
                        return (
                            <div key={i} className="d-flex align-items-center gap-3 py-2" style={{ borderBottom: i < oportunidades.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                                <span style={{ color: "#999", fontSize: "0.85rem", fontWeight: 600, width: 14 }}>{i + 1}</span>
                                <span style={{ color: "#666", display: "inline-flex", flexShrink: 0 }}>{o.icono}</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#222" }}>{o.titulo}</div>
                                    <div style={{ fontSize: "0.74rem", color: "#666" }}>{o.detalle}</div>
                                </div>
                                <span style={{
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    padding: "3px 10px",
                                    borderRadius: 12,
                                    backgroundColor: p.bg,
                                    color: p.color,
                                }}>{o.prioridad}</span>
                            </div>
                        );
                    })}
                    <button className="btn btn-sm mt-3" style={{
                        fontSize: "0.78rem",
                        border: "1px solid #ddd",
                        color: "#555",
                        fontWeight: 500,
                    }}>
                        Ver todas las recomendaciones →
                    </button>
                </div>
            </div>
        </div>
    );
}
